import { Building2, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useGetProjectsQuery } from '../../services/projectApi';
import { Project } from '../../types';

interface ProjectSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetRoute: string;
  moduleLabel: string;
}

const ProjectSelectionModal: React.FC<ProjectSelectionModalProps> = ({
  isOpen,
  onClose,
  targetRoute,
  moduleLabel,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Use RTK Query for cached project fetching
  const { data: projectsData, isLoading } = useGetProjectsQuery(
    { page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' },
    { skip: !isOpen }
  );

  // Extract projects from response
  const projects = useMemo(() => {
    if (projectsData?.success && projectsData.data?.projects) {
      return projectsData.data.projects;
    }
    return [];
  }, [projectsData]);

  // Filter projects based on search
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) {
      return projects;
    }
    return projects.filter(
      project =>
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, projects]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, filteredProjects.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredProjects[selectedIndex]) {
            handleProjectSelect(filteredProjects[selectedIndex]);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredProjects, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleProjectSelect = useCallback(
    (project: Project) => {
      if (!project._id) return;

      // Update recent projects
      const recent = JSON.parse(localStorage.getItem('bb.recentProjects') || '[]');
      const newRecent = [project._id, ...recent.filter((id: string) => id !== project._id)].slice(
        0,
        5
      );
      localStorage.setItem('bb.recentProjects', JSON.stringify(newRecent));

      // Navigate to the target route with the selected project
      const finalRoute = targetRoute.replace(':projectId', project._id);
      navigate(finalRoute);
      onClose();
    },
    [targetRoute, navigate, onClose]
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center"
      onClick={e => e.target === overlayRef.current && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Select a Project</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Choose a project to view <span className="font-medium text-brand-600">{moduleLabel}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setSelectedIndex(0);
              }}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              aria-label="Search projects"
            />
          </div>
        </div>

        {/* Project List - always visible */}
        <div className="px-2 pb-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin h-5 w-5 border-2 border-brand-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-sm text-gray-600">Loading projects...</span>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Building2 className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">
                {searchQuery ? 'No projects found' : 'No projects available'}
              </p>
            </div>
          ) : (
            <div ref={listRef} className="overflow-y-auto max-h-72" role="listbox">
              {filteredProjects.map((project, index) => {
                const isSelected = index === selectedIndex;

                return (
                  <div
                    key={project._id}
                    className={`px-4 py-3 cursor-pointer rounded-lg mx-1 my-0.5 transition-colors ${
                      isSelected ? 'bg-brand-50 ring-1 ring-brand-200' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleProjectSelect(project)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {project.name}
                        </h4>
                        {project.location?.city && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {project.location.city}
                            {project.location.state && `, ${project.location.state}`}
                          </p>
                        )}
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ml-2 ${
                          project.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : project.status === 'planning'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {project.status || 'Planning'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Keyboard hints */}
        <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-center text-xs text-gray-400 space-x-4 bg-gray-50">
          <span>↑↓ Navigate</span>
          <span>Enter Select</span>
          <span>ESC Cancel</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProjectSelectionModal;
